import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Popover,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import React, { useState } from 'react';
import { useQuery as useReactQuery, useQueryClient } from 'react-query';
import {
  useCanChangeReviewStatus,
  useCanReadNotes,
  useReviewStatusFeatureFlag,
} from '@dispatch/shared/hooks';
import {
  getReviewStatus,
  getReviewStatusHistory,
  ReviewStatusDto,
  ReviewStatusHistoryItem,
  ReviewStatusState,
  setReviewStatus,
} from '@dispatch/shared/services';

export interface ReviewStatusToggleProps {
  taskId: string;
  tenantId?: string;
  onError?: (e: unknown) => void;
}

const formatTs = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString();
};

/**
 * ReviewStatusToggle - 3-state render matrix:
 *   1. Feature off or no read permission -> null
 *   2. Read only -> static text
 *   3. Change permission -> interactive Switch with optimistic update + rollback
 */
export const ReviewStatusToggle: React.FC<ReviewStatusToggleProps> = ({
  taskId,
  tenantId,
  onError,
}) => {
  const featureOn = useReviewStatusFeatureFlag(tenantId);
  const canRead = useCanReadNotes();
  const canChange = useCanChangeReviewStatus();
  const queryClient = useQueryClient();

  const queryKey = ['review-status', taskId];
  const { data, isLoading } = useReactQuery<ReviewStatusDto>(
    queryKey,
    () => getReviewStatus(taskId),
    {
      enabled: !!taskId && featureOn && canRead,
    }
  );

  const [optimistic, setOptimistic] = useState<ReviewStatusState | null>(null);
  const [saving, setSaving] = useState(false);
  const [historyAnchor, setHistoryAnchor] = useState<HTMLElement | null>(null);
  const [history, setHistory] = useState<ReviewStatusHistoryItem[] | null>(
    null
  );
  const [historyError, setHistoryError] = useState(false);

  if (!featureOn || !canRead) return null;

  const effectiveState: ReviewStatusState =
    optimistic ?? data?.state ?? 'NotReviewed';

  // Read-only render
  if (!canChange) {
    if (isLoading)
      return <CircularProgress size={16} data-testid="review-status-loading" />;
    if (!data) return null;
    return (
      <Box data-testid="review-status-readonly">
        <Typography variant="body2">
          {data.state === 'Reviewed'
            ? `Reviewed by ${data.lastChangedBy ?? 'unknown'} · ${formatTs(
                data.lastChangedAtUtc
              )}`
            : 'Not reviewed'}
        </Typography>
      </Box>
    );
  }

  // Interactive render with optimistic update
  const handleToggle = async (next: ReviewStatusState) => {
    if (saving) return;
    const prev = data?.state ?? 'NotReviewed';
    setOptimistic(next);
    setSaving(true);
    try {
      const result = await setReviewStatus(taskId, next);
      queryClient.setQueryData(queryKey, result);
      setOptimistic(null);
      // Invalidate the cached history so reopening the popover refetches and
      // shows the entry this toggle just created (openHistory short-circuits
      // while `history` is non-null).
      setHistory(null);
    } catch (e) {
      // Roll back optimistic update.
      setOptimistic(prev);
      // Then clear after a tick so the cached source-of-truth wins.
      setTimeout(() => setOptimistic(null), 0);
      onError?.(e);
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async (e: React.MouseEvent<HTMLElement>) => {
    setHistoryAnchor(e.currentTarget);
    // Already loaded successfully — keep the cached history, don't refetch.
    if (history !== null) return;
    // Clear any prior failure so reopening the popover retries the fetch
    // (previously historyError latched and blocked retries until reload).
    setHistoryError(false);
    try {
      const items = await getReviewStatusHistory(taskId);
      setHistory(items);
    } catch {
      setHistoryError(true);
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      data-testid="review-status-toggle"
    >
      <Typography variant="body2">Reviewed</Typography>
      <Switch
        checked={effectiveState === 'Reviewed'}
        onChange={(_, checked) =>
          handleToggle(checked ? 'Reviewed' : 'NotReviewed')
        }
        disabled={saving}
        data-testid="review-status-switch"
        slotProps={{ input: { 'aria-label': 'Review status' } }}
      />
      {data?.lastChangedBy && (
        <Typography variant="caption" color="text.secondary">
          {`${data.lastChangedBy} · ${formatTs(data.lastChangedAtUtc)}`}
        </Typography>
      )}
      <IconButton
        size="small"
        onClick={openHistory}
        data-testid="review-status-history-button"
        aria-label="Review status history"
      >
        <HistoryIcon fontSize="small" />
      </IconButton>
      <Popover
        open={!!historyAnchor}
        anchorEl={historyAnchor}
        onClose={() => setHistoryAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        data-testid="review-status-history-popover"
      >
        <Box sx={{ padding: '8px 12px', minWidth: 220 }}>
          {historyError && (
            <Typography
              variant="body2"
              color="error"
              data-testid="review-status-history-error"
            >
              Couldn&apos;t load history.
            </Typography>
          )}
          {!historyError && history === null && <CircularProgress size={16} />}
          {!historyError && history !== null && history.length === 0 && (
            <Typography variant="body2">No history</Typography>
          )}
          {!historyError &&
            history !== null &&
            history.map((h, idx) => (
              <Box key={idx} sx={{ padding: '4px 0' }}>
                <Typography variant="caption">
                  {`${h.fromState} → ${h.toState}`}
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  {`${h.changedBy ?? 'unknown'} · ${formatTs(h.changedAtUtc)}`}
                </Typography>
              </Box>
            ))}
          <Stack alignItems="flex-end" sx={{ marginTop: 1 }}>
            <Button size="small" onClick={() => setHistoryAnchor(null)}>
              Close
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Stack>
  );
};
